/**
 * @fileoverview Ask OBN page for natural language command parsing
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseCommand, parseCommandTools, parseCommandMulti, assessCommand, getSupportedIntents } from '../api/commands';
import {
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CpuChipIcon,
  QuestionMarkCircleIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  Square3Stack3DIcon
} from '@heroicons/react/24/outline';

const MAX_COMMAND_LENGTH = 200;

function AskObnPage() {
  const [command, setCommand] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [assessResult, setAssessResult] = useState(null);
  const [error, setError] = useState(null);
  const [showExamples, setShowExamples] = useState(false);
  const [parserType, setParserType] = useState('intent'); // 'intent', 'tools', or 'multi'
  const [disableCache, setDisableCache] = useState(false);

  // Get supported intents for examples
  const { data: intentsData } = useQuery({
    queryKey: ['supportedIntents'],
    queryFn: getSupportedIntents,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Auto-assess command as user types (debounced)
  useEffect(() => {
    if (command.length < 3) {
      setAssessResult(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const result = await assessCommand(command);
        setAssessResult(result);
      } catch {
        setAssessResult(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [command]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!command.trim()) return;

    setIsLoading(true);
    setError(null);
    setParseResult(null);

    try {
      let result;
      if (parserType === 'tools') {
        result = await parseCommandTools(command.trim(), { disableCache });
      } else if (parserType === 'multi') {
        result = await parseCommandMulti(command.trim(), { disableCache });
      } else {
        result = await parseCommand(command.trim(), { disableCache });
      }
      setParseResult({ ...result, parserType });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse command');
    } finally {
      setIsLoading(false);
    }
  };

  const getIntentColor = (intent, confidence) => {
    if (intent === 'unknown' || confidence < 0.3) {
      return 'text-red-600 bg-red-50';
    } else if (confidence < 0.7) {
      return 'text-yellow-600 bg-yellow-50';
    } else {
      return 'text-green-600 bg-green-50';
    }
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 0.8) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else if (confidence >= 0.5) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    } else {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const renderExamples = () => {
    if (!intentsData?.intents) return null;

    const exampleIntents = Object.values(intentsData.intents).slice(0, 6);

    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <SparklesIcon className="h-5 w-5 text-blue-500 mr-2" />
          Example Commands
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exampleIntents.map((intent, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-2">
                {intent.value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h4>
              <div className="space-y-1">
                {intent.examples.slice(0, 2).map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setCommand(example)}
                    className="block text-left text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-2 py-1 rounded transition-colors"
                  >
                    "{example}"
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center mb-2">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-500 mr-3" />
          Ask OBN
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Use natural language to interact with your newsletter content. Try commands like
          "read me the next article" or "classify this as tutorial".
        </p>
      </div>

      {/* Parser Type Selection */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:ring-1 dark:ring-white/10 p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Parser Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="parserType"
                value="intent"
                checked={parserType === 'intent'}
                onChange={(e) => setParserType(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Intent-Based</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Single intent per command, simple operations
                </p>
              </div>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="parserType"
                value="multi"
                checked={parserType === 'multi'}
                onChange={(e) => setParserType(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <Square3Stack3DIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Multi-Intent</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Multiple intents per command, compound actions
                </p>
              </div>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="parserType"
                value="tools"
                checked={parserType === 'tools'}
                onChange={(e) => setParserType(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Tool-Based</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Multiple tools per command, complex operations
                </p>
              </div>
            </label>
          </div>

          {/* Cache Control */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={disableCache}
                onChange={(e) => setDisableCache(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Disable Cache</span>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 ml-1" title="Bypass intent cache for fresh LLM responses (slower but tests raw performance)" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Bypass cache for fresh LLM responses (tests raw parsing performance)
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Command Input Form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:ring-1 dark:ring-white/10">
          <div className="p-6">
            <label htmlFor="command" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Your Command
            </label>
            <div className="relative">
              <textarea
                id="command"
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Type your command here... (e.g., 'read me the next unread article'). Press Enter to submit, Shift+Enter for new line."
                className="w-full px-4 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent dark:focus:border-blue-400 resize-none"
                rows={3}
                maxLength={MAX_COMMAND_LENGTH}
                disabled={isLoading}
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500">
                {command.length}/{MAX_COMMAND_LENGTH}
              </div>
            </div>

            {/* Real-time assessment */}
            {assessResult && (
              <div className="mt-3 flex items-center space-x-2 text-sm">
                {assessResult.supported ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    <span className="text-green-700 dark:text-green-300">
                      Looks supported ({Math.round(assessResult.confidence * 100)}% confidence)
                    </span>
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                    <span className="text-yellow-700 dark:text-yellow-300">
                      Might not be supported ({assessResult.reason})
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => setShowExamples(!showExamples)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
              >
                <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
                {showExamples ? 'Hide Examples' : 'Show Examples'}
              </button>

              <button
                type="submit"
                disabled={!command.trim() || isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Parse Command
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Examples Section */}
      {showExamples && renderExamples()}

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-800 dark:text-red-200 font-medium">Error</span>
          </div>
          <p className="text-red-700 dark:text-red-300 mt-1">{error}</p>
        </div>
      )}

      {/* Parse Results */}
      {parseResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:ring-1 dark:ring-white/10">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <CpuChipIcon className="h-5 w-5 text-blue-500 mr-2" />
              Parse Results
              <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 px-2 py-1 rounded">
                {parseResult.parserType === 'tools' ? 'Tool-Based' :
                 parseResult.parserType === 'multi' ? 'Multi-Intent' : 'Intent-Based'}
              </span>
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Multi-Intent Results */}
            {parseResult.parserType === 'multi' && parseResult.intents ? (
              <>
                {/* Multiple Intents */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Intents Identified</h3>
                  <div className="space-y-3">
                    {parseResult.intents.map((intent, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Square3Stack3DIcon className="h-4 w-4 text-green-500" />
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIntentColor(intent.intent, intent.confidence)}`}>
                              {intent.intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            {parseResult.primaryIntent === intent.intent && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 px-2 py-1 rounded">Primary</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {getConfidenceIcon(intent.confidence)}
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {Math.round(intent.confidence * 100)}% confidence
                            </span>
                          </div>
                        </div>
                        {intent.parameters && Object.keys(intent.parameters).length > 0 && (
                          <div className="mt-2">
                            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Parameters:</h4>
                            <div className="bg-gray-50 dark:bg-gray-900 rounded p-2">
                              <pre className="text-xs text-gray-700 dark:text-gray-200">
                                {JSON.stringify(intent.parameters, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                        {intent.explanation && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 italic">
                            {intent.explanation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Global Parameters */}
                {parseResult.globalParameters && Object.keys(parseResult.globalParameters).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Shared Parameters</h3>
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                      <pre className="text-sm text-blue-800 dark:text-blue-200">
                        {JSON.stringify(parseResult.globalParameters, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Execution Order */}
                {parseResult.executionOrder && parseResult.executionOrder.length > 1 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Execution Order</h3>
                    <div className="flex items-center space-x-2">
                      {parseResult.executionOrder.map((intent, index) => (
                        <div key={index} className="flex items-center">
                          <span className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 rounded">
                            {index + 1}. {intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          {index < parseResult.executionOrder.length - 1 && (
                            <span className="mx-2 text-gray-400 dark:text-gray-500">→</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Context Required */}
                {parseResult.requiresContext && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                      <span className="text-yellow-800 dark:text-yellow-200 font-medium">Context Required</span>
                    </div>
                    <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                      Some intents require context (like current item or issue) to execute properly.
                    </p>
                  </div>
                )}
              </>
            ) : /* Tool-Based Results */
            parseResult.parserType === 'tools' && parseResult.tools ? (
              <>
                {/* Tools Used */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Tools Used</h3>
                  <div className="space-y-3">
                    {parseResult.tools.map((tool, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <WrenchScrewdriverIcon className="h-4 w-4 text-blue-500" />
                            <span className="font-medium text-gray-900 dark:text-gray-100">{tool.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getConfidenceIcon(tool.confidence)}
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {Math.round(tool.confidence * 100)}% confidence
                            </span>
                          </div>
                        </div>
                        {tool.parameters && Object.keys(tool.parameters).length > 0 && (
                          <div className="mt-2">
                            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Parameters:</h4>
                            <div className="bg-gray-50 dark:bg-gray-900 rounded p-2">
                              <pre className="text-xs text-gray-700 dark:text-gray-200">
                                {JSON.stringify(tool.parameters, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                        {tool.summary && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 italic">
                            {tool.summary}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overall Summary */}
                {parseResult.summary && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Summary</h3>
                    <p className="text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                      {parseResult.summary}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Intent-Based Results */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Intent</h3>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getIntentColor(parseResult.intent, parseResult.confidence)}`}>
                      {parseResult.intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    {getConfidenceIcon(parseResult.confidence)}
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {Math.round(parseResult.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>

                {/* Parameters */}
                {parseResult.parameters && Object.keys(parseResult.parameters).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Parameters</h3>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <pre className="text-sm text-gray-800 dark:text-gray-200">
                        {JSON.stringify(parseResult.parameters, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Explanation */}
                {parseResult.explanation && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Explanation</h3>
                    <p className="text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                      {parseResult.explanation}
                    </p>
                  </div>
                )}

                {/* Context Required */}
                {parseResult.requiresContext && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                      <span className="text-yellow-800 dark:text-yellow-200 font-medium">Context Required</span>
                    </div>
                    <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                      This command requires context (like current item or issue) to execute properly.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Technical Details */}
            {parseResult.usage && (
              <details className="bg-gray-50 dark:bg-gray-900 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Technical Details
                </summary>
                <div className="px-4 pb-4 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-300">Input Tokens:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{parseResult.usage.inputTokens}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-300">Output Tokens:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{parseResult.usage.outputTokens}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-300">Model:</span>
                      <span className="ml-2 font-medium text-xs text-gray-900 dark:text-gray-100">{parseResult.modelId?.split(':')[0] || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-300">Latency:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{parseResult.latencyMs}ms</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-300">Cached:</span>
                      <span className={`ml-2 font-medium ${parseResult.cached ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}>
                        {parseResult.cached ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </details>
            )}

            {/* Errors */}
            {parseResult.errors && parseResult.errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Errors</h3>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  {parseResult.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AskObnPage;